import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CONFIG_TOKEN, DeviceType, FormType, ICOP_BANNER_LOCATIONS, PromotionalEligiblePlanIds } from 'core/constants';
import { FormUtil } from 'core/utils/form';
import { Subscription } from 'rxjs';
import { CimaCore } from 'services/index';
import { XmStore, XmStoreUtil } from 'services/store';
import { SelectedPlanService } from 'shared/services/selected-plan';
import { LineSummary } from 'store/account-summary/models';
import { LevelMoveEligibility } from 'store/level-move/models';
import { BillingBannerInfo } from 'store/billing-banner/models';
import { IXMOptions } from 'core/interfaces';
import { StoreAction } from 'store/actions';
import { CmsCore } from 'store/cms/models';
import { Cloudinary } from 'core/services';

@Component({
    selector: 'xm-choose-data-options',
    templateUrl: './choose-data-options.html',
    styleUrls: ['./choose-data-options.scss'],
    standalone: false
})
export class XmChooseDataOptions implements OnInit, OnDestroy {
    @Input() public lines: LineSummary[];
    @Input() public pageData: CmsPlanOverviewChooseDataOptions;
    // Preserve the public output name used by parent templates.
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() public submit: EventEmitter<LevelMoveOverride[]> = new EventEmitter<LevelMoveOverride[]>();
    public isBusinessUser: boolean;
    public planTypeOptions: XmInputOptions;
    public planTypeform: FormGroup;
    public dropdowns: FormArray<FormControl>;
    public enableICOPPhaseOneSegSwitch: boolean;
    public isBuyFLowConvergenceEnabled: boolean = false;
    public promotionsBanner: BillingBanner;
    public infoIconGrey: MediaImageOptions;


    private subscriptions: Subscription[] = [];
    private xmStore: XmStore;
    private cimaCore: CimaCore;
    private eligibleRecommendations: LevelMoveRecommendation[];
    private dropDownSelections: DropdownOutput[] = [];
    private userSelection: LevelMoveOverride[];
    private selectedPlanService: SelectedPlanService;
    private config: IXMOptions;

    constructor(cimaCore: CimaCore, xmStore: XmStore, fb: FormBuilder, selectedPlanService: SelectedPlanService, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { cimaCore, xmStore, selectedPlanService, config });
        this.dropdowns = fb.array([]);
        this.planTypeform = fb.group({
            dropdowns: this.dropdowns
        });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.enableICOPPhaseOneSegSwitch = this.config.ENABLE_ICOP_P1_SEGMENTSWITCH;
        this.isBuyFLowConvergenceEnabled = this.config.ENABLE_BUYFLOW_CONVERGENCE_URL;
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<LevelMoveEligibility>(LevelMoveEligibility), (response: LevelMoveEligibility) => {
            if (response.isEligible) {
                this.eligibleRecommendations = response.recommendations;
            }
            this.setDataOptions();
        }, () => {
            this.setDataOptions();
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (cms: CmsCore) => {
            this.infoIconGrey = {
                mobile: Cloudinary.generateMediaFromCms(cms.icons.infoIconGreySmall)
            };
        }));

        if (this.enableICOPPhaseOneSegSwitch) {
            this.subscriptions.push(XmStoreUtil.subscribe(
                this.xmStore.find<BillingBannerInfo>(StoreAction.GET_BILLING_BANNER, BillingBannerInfo), (bannerInfo: BillingBannerInfo) => {
                    if (bannerInfo && bannerInfo.banners) {
                        this.promotionsBanner = bannerInfo.banners.find((banner: BillingBanner) => banner.location === ICOP_BANNER_LOCATIONS.SEG_SWITCH_START);
                    }
                }));
        }

    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }

    public validate(): void {
        FormUtil.submitForm(this.planTypeform, this.continue.bind(this));
    }

    public openPromoModal(): void {
        // To be implemented when CEUIDSS-39695 content is finalized
    }

    /**
     * 
     * @param dropdownOutput 
     */
    /**
     * Handles the selection event from a dropdown.
     * 
     * This method updates the `dropDownSelections` array with the new selection.
     * If the selection already exists in the array (based on `otherData (lineId)`), it updates the existing entry.
     * Otherwise, it adds the new selection to the array.
     * 
     * @param dropdownOutput - The output object from the dropdown selection containing the new selection data.
     */
    public onDropDownSelection(dropdownOutput: DropdownOutput): void {
        const index: number = this.dropDownSelections.findIndex((item: DropdownOutput) => item.selection.otherData === dropdownOutput.selection.otherData);
        if (index !== -1) {
            this.dropDownSelections[index] = dropdownOutput;
        } else {
            this.dropDownSelections.push(dropdownOutput);
        }
    }

    private continue(): void {
        if (this.planTypeform.valid) {
            this.userSelection = this.getUserSelection();
            this.submit.emit(this.userSelection);
        }
    }


    /**
     * Retrieves the user's selection of level move overrides.
     *
     * This method constructs an array of `LevelMoveOverride` objects based on the user's
     * dropdown selections and the lines that are not editable. It maps the dropdown
     * selections to `LevelMoveOverride` objects.
     *
     * @returns {LevelMoveOverride[]} An array of `LevelMoveOverride` objects representing
     * the user's selection.
     */
    private getUserSelection(): LevelMoveOverride[] {
        const selections: LevelMoveOverride[] = this.dropDownSelections.map((selection: DropdownOutput) => ({
            lineId: selection.selection.otherData,
            planId: selection.selection.value
        }));

        const dropdownLineIds: string[] = this.dropDownSelections.map((selection: DropdownOutput) => selection.selection.otherData);

        const tabletSelections: LevelMoveOverride[] = this.lines
            .filter((line: LineSummary) => line.isActive && !dropdownLineIds.includes(line.id.value) && line.isTablet)
            .map((line: LineSummary) => ({
                lineId: line.id.value,
                planId: line.dataOptions?.defaultOption?.[0]?.value ?? undefined
            }));

        const watchSelections: LevelMoveOverride[] = this.lines
            .filter((line: LineSummary) => line.isActive && !dropdownLineIds.includes(line.id.value) && line.isWatch)
            .map((line: LineSummary) => ({
                lineId: line.id.value,
                planId: line.isStandaloneWatch ? this.pageData.plans.smartwatch.resi.standalone.value : this.pageData.plans.smartwatch.resi.paired.value
            }));

        const allSelections: LevelMoveOverride[] = [...selections, ...tabletSelections, ...watchSelections];

        this.selectedPlanService.saveSelectedPlans(allSelections);
        
        return allSelections;
    }

    /**
     * Sets the data options for each line in the `lines` array based on the device type and user type.
     * 
     * - For `DeviceType.PHONE`, it maps the phone plans to the line's data options.
     * - For `DeviceType.TABLET`, it maps either business or residential tablet plans to the line's data options based on the user type.
     * - For `DeviceType.SMART_WATCH`, it sets the data options to undefined.
     * 
     * Additionally, it sets promotional content for eligible lines and initializes dropdowns.
     * 
     * @private
     */
    private setDataOptions(): void {
        this.planTypeOptions = {
            inputId: 'plan-type-dropdown',
            header: '',
            errors: this.pageData.dataOptionErrors
        };

        this.lines.forEach((line: LineSummary) => {
            line.promotionContent = this.isPromotionalEligible(line) ? this.pageData.promotionContent : undefined;
            if (line.isActive && line.device.deviceType === DeviceType.PHONE) {
                const options: DropdownItem[] = this.pageData.plans.phone.map((option: DropdownItem) => ({
                    ...option,
                    otherData: line.id.value
                }));
                line.dataOptions = {
                    options,
                    isEditable: true
                };
            } else if (line.isActive && line.device.deviceType === DeviceType.TABLET) {
                if (this.isBusinessUser) {
                    const options: DropdownItem[] = this.pageData.plans.tablet.cbm.map((option: DropdownItem) => ({
                        ...option,
                        otherData: line.id.value
                    }));
                    line.dataOptions = {
                        options,
                        isEditable: true
                    };
                } else {
                    const options: DropdownItem[] = this.pageData.plans.tablet.resi.map((option: DropdownItem) => ({
                        ...option,
                        otherData: line.id.value
                    }));
                    line.dataOptions = {
                        options,
                        isEditable: false,
                        defaultOption: options
                    };
                }
            } else if (line.isActive && line.device.deviceType === DeviceType.SMART_WATCH) {
                line.dataOptions = undefined;
            }
        });

        this.initializeDropDowns();
    }


    /**
     * Initializes the dropdowns for each line in the `lines` array.
     * 
     * This method iterates over the `lines` array and creates a `FormControl` for each line based on the presence of `dataOptions`.
     * If `dataOptions` has a `defaultOption`, the `FormControl` is initialized with the first value of `defaultOption`.
     * If `dataOptions` is not present, the `FormControl` is initialized with an empty string.
     * Otherwise, the `FormControl` is initialized with an empty string and validators.
     * The created `FormControl` is then added to the `dropdowns` array.
     * 
     * @private
     */
    private initializeDropDowns(): void {
        this.lines.forEach((line: LineSummary) => {
            let control: FormControl;
            if (line.dataOptions?.defaultOption) {
                control = new FormControl(line.dataOptions?.defaultOption[0].value, { nonNullable: true, validators: FormUtil.validators(this.planTypeOptions.errors), updateOn: FormType.blur });
            } else if (!line.dataOptions) {
                control = new FormControl('');
            } else {
                control = new FormControl('', { validators: FormUtil.validators(this.planTypeOptions.errors), updateOn: FormType.blur });
            }
            this.dropdowns.push(control);
        });
    }


    /**
     * Determines if a given line is eligible for promotional offers.
     *
     * @param line - The line summary object containing details about the line.
     * @returns `true` if the line is eligible for promotional offers, otherwise `false`.
     *
     * This method checks the following conditions to determine eligibility:
     * 1. Checks if there are any eligible recommendations.
     * 2. If eligible recommendations exist, it checks if the line ID matches any of the recommendation line IDs.
     * 3. It also checks if the plan ID of the line is included in the list of promotional eligible plan IDs.
     * 4. If both the line ID and plan ID match the criteria, the line is considered eligible for promotional offers.
     */
    private isPromotionalEligible(line: LineSummary): boolean {
        if (this.eligibleRecommendations && this.eligibleRecommendations?.length) {
            const foundRecommendation: LevelMoveRecommendation = this.eligibleRecommendations.find((recommendation: LevelMoveRecommendation) => recommendation.lineId === line.id.value);
            const foundPlanId: boolean = PromotionalEligiblePlanIds.includes(foundRecommendation.planId);
            if (foundRecommendation && foundPlanId) {
                return true;
            }
        }

        return false;
    }
}
